import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Mail, Lock, ArrowRight, X } from 'lucide-react';

export const AuthModal: React.FC = () => {
  const { 
    login, 
    loginWithEmail, 
    registerWithEmail, 
    resetPassword, 
    isLoggingIn, 
    authError, 
    clearAuthError,
    isAuthModalOpen,
    authMode,
    closeAuth
  } = useAuth();
  
  const [localMode, setLocalMode] = useState<'login' | 'register' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Sync with global authMode when modal opens
  useEffect(() => {
    if (isAuthModalOpen) {
      setLocalMode(authMode);
      setSuccessMsg(null);
    }
  }, [isAuthModalOpen, authMode]);

  if (!isAuthModalOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg(null);
    try {
      if (localMode === 'login') {
        await loginWithEmail(email, password);
        closeAuth();
      } else if (localMode === 'register') {
        await registerWithEmail(email, password);
        closeAuth();
      } else if (localMode === 'forgot') {
        await resetPassword(email);
        setSuccessMsg('Password reset email sent! Check your inbox.');
        setTimeout(() => setLocalMode('login'), 3000);
      }
    } catch (err: any) {
      console.error('Auth error:', err?.message || err);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await login();
      closeAuth();
    } catch (err: any) {
      console.error('Google login error:', err?.message || err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-start sm:justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300 overflow-y-auto">
      <div className="relative w-full max-w-md bg-bg-alt border border-border-main rounded-3xl p-6 sm:p-8 shadow-2xl animate-in zoom-in-95 duration-300 my-8 sm:my-auto shrink-0">
        <button 
          onClick={closeAuth}
          className="absolute top-6 right-6 p-2 text-text-alt hover:text-text-main transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-text-main mb-2">
            {localMode === 'login' ? 'Welcome Back' : localMode === 'register' ? 'Create Account' : 'Reset Password'}
          </h2>
          <p className="text-text-alt text-sm">
            {localMode === 'login' ? 'Login to continue your learning journey' : localMode === 'register' ? 'Start turning content into understanding' : 'Enter your email to receive a reset link'}
          </p>
        </div>

        {authError && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-500 text-sm flex items-center gap-3">
             <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
             {authError}
          </div>
        )}

        {successMsg && (
          <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-emerald-500 text-sm">
             {successMsg}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-xs font-bold text-text-alt opacity-60 uppercase tracking-widest ml-1">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-alt opacity-50" />
              <input 
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                className="w-full pl-12 pr-4 py-4 bg-bg-main border border-border-main rounded-2xl text-text-main placeholder:text-text-alt/30 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
              />
            </div>
          </div>

          {localMode !== 'forgot' && (
            <div className="space-y-2">
              <div className="flex justify-between items-center ml-1">
                <label className="text-xs font-bold text-text-alt opacity-60 uppercase tracking-widest">Password</label>
                {localMode === 'login' && (
                  <button 
                    type="button"
                    onClick={() => setLocalMode('forgot')}
                    className="text-xs text-indigo-500 hover:text-indigo-400 font-bold"
                  >
                    Forgot?
                  </button>
                )}
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-alt opacity-50" />
                <input 
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-12 pr-4 py-4 bg-bg-main border border-border-main rounded-2xl text-text-main placeholder:text-text-alt/30 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                />
              </div>
            </div>
          )}

          <button 
            type="submit"
            disabled={isLoggingIn}
            className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-70 text-white font-bold rounded-2xl transition-all flex items-center justify-center gap-2 shadow-xl shadow-indigo-500/20 active:scale-95"
          >
            {isLoggingIn ? <Loader2 className="w-5 h-5 animate-spin" /> : (
              <>
                {localMode === 'login' ? 'Login' : localMode === 'register' ? 'Register' : 'Send Reset Link'}
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </form>

        <div className="relative my-8">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border-main"></div>
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-bg-alt px-4 text-text-alt opacity-50 font-bold tracking-widest">Or continue with</span>
          </div>
        </div>

        <button 
          onClick={handleGoogleLogin}
          disabled={isLoggingIn}
          className="w-full py-4 bg-bg-main border border-border-main text-text-main font-bold rounded-2xl transition-all flex items-center justify-center gap-3 active:scale-95 disabled:opacity-70 hover:bg-bg-alt"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Google Account
        </button>

        <div className="mt-8 text-center">
          <button 
            type="button"
            onClick={() => {
              setLocalMode(localMode === 'login' ? 'register' : 'login');
              clearAuthError();
            }}
            className="text-sm text-text-alt hover:text-text-main transition-colors"
          >
            {localMode === 'login' ? "Don't have an account? " : "Already have an account? "}
            <span className="text-indigo-500 font-bold">{localMode === 'login' ? 'Sign up' : 'Log in'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
